import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Award, Users, Star, Heart, CheckCircle } from "lucide-react";
import Footer from "@/components/Footer";

export default function AboutUs() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-600 via-purple-600 to-pink-600 text-white py-6">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            About MUAS Academy
          </h1>
          <p className="text-white/80 mt-2">Ranchi's Premier Makeup & Beauty Training Institute</p>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Empowering Beauty Professionals Since Day One
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                MUAS Academy (Makeup Artist Academy) is Ranchi's leading professional makeup and beauty training institute. Founded by renowned bridal makeup artist <strong>Aradhana Shreya</strong>, we are dedicated to transforming passionate individuals into skilled, confident beauty professionals.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Located in the heart of Ranchi at Harmu Housing Colony, our academy offers comprehensive courses in bridal makeup, HD techniques, airbrush application, hairstyling, and more. We combine traditional artistry with modern techniques to give our students a competitive edge in the beauty industry.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether you're a complete beginner or looking to enhance your existing skills, MUAS Academy provides personalized training, hands-on practice, and industry-ready certification to help you achieve your dreams.
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-8">
              <img 
                src="/coach-slider/sh1.jpg" 
                alt="MUAS Academy Training"
                className="rounded-xl shadow-lg w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Meet Our Founder
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The visionary behind MUAS Academy's success
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-1">
                <img 
                  src="/coach-slider/sh2.jpg" 
                  alt="Aradhana Shreya - Founder"
                  className="rounded-xl shadow-lg w-full h-64 object-cover"
                />
              </div>
              <div className="md:col-span-2">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Aradhana Shreya</h3>
                <p className="text-pink-600 font-medium mb-4">Founder & Lead Makeup Artist</p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Aradhana Shreya is a celebrated bridal makeup artist with years of experience in creating stunning, high-definition wedding looks. Her passion for makeup artistry and dedication to education led her to establish MUAS Academy, with the vision of nurturing the next generation of beauty professionals in Jharkhand.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Specializing in HD bridal makeup, airbrush techniques, and engagement styling, Aradhana has transformed hundreds of brides on their special day. Her expertise, combined with a warm teaching approach, makes learning at MUAS Academy an enriching experience.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm">HD Bridal Specialist</span>
                  <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">Airbrush Expert</span>
                  <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm">Celebrity MUA</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Why Choose MUAS Academy?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We provide more than just training – we build careers
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
              <Award className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Industry-Recognized Certification</h3>
              <p className="text-gray-600">Our certificates are recognized across the beauty industry, opening doors to employment and freelance opportunities.</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
              <Users className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Small Batch Sizes</h3>
              <p className="text-gray-600">We maintain small class sizes to ensure personalized attention and hands-on practice for every student.</p>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
              <Star className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Expert Faculty</h3>
              <p className="text-gray-600">Learn directly from working professionals with real-world experience in bridal and fashion makeup.</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
              <Heart className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Practical Training</h3>
              <p className="text-gray-600">80% of our curriculum is hands-on practice on real models, preparing you for actual client work.</p>
            </div>
            
            <div className="bg-gradient-to-br from-pink-50 to-white p-6 rounded-xl border border-pink-100">
              <CheckCircle className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Career Support</h3>
              <p className="text-gray-600">We provide placement assistance, portfolio building guidance, and business development tips for freelancers.</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl border border-purple-100">
              <Award className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Premium Products</h3>
              <p className="text-gray-600">Train with the same professional-grade products used by top makeup artists in the industry.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Courses */}
      <section className="py-16 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Courses
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive programs designed for every skill level
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Professional Bridal Makeup Course</h3>
              <p className="text-gray-600 mb-4">Master HD bridal makeup, engagement looks, and reception styling with our comprehensive bridal program.</p>
              <Link to="/courses" className="text-pink-600 font-medium hover:text-pink-700">Learn More →</Link>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Self Makeup Course</h3>
              <p className="text-gray-600 mb-4">Learn professional techniques to do your own makeup for any occasion – from daily wear to parties.</p>
              <Link to="/courses" className="text-pink-600 font-medium hover:text-pink-700">Learn More →</Link>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Advanced Hairstyling Course</h3>
              <p className="text-gray-600 mb-4">From classic updos to trendy hairstyles – become a complete bridal artist with our hair styling module.</p>
              <Link to="/courses" className="text-pink-600 font-medium hover:text-pink-700">Learn More →</Link>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Complete Diploma Program</h3>
              <p className="text-gray-600 mb-4">Our flagship program covering all aspects of professional makeup artistry – from basics to advanced techniques.</p>
              <Link to="/courses" className="text-pink-600 font-medium hover:text-pink-700">Learn More →</Link>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Link 
              to="/courses" 
              className="inline-block bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-3 rounded-full font-medium hover:from-pink-700 hover:to-purple-700 transition-all"
            >
              View All Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Visit Our Academy
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Conveniently located in Harmu, Ranchi
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h3>
                <div className="space-y-3 text-gray-600">
                  <p><strong>Address:</strong><br />C-127, Harmu Housing Colony,<br />Harmu Ranchi 834002,<br />Jharkhand, India</p>
                  <p><strong>Phone:</strong><br />
                    <a href="tel:+916201542274" className="text-pink-600 hover:text-pink-700">+91 6201542274</a><br />
                    <a href="tel:+919773682082" className="text-pink-600 hover:text-pink-700">+91 9773682082</a>
                  </p>
                  <p><strong>Email:</strong><br />
                    <a href="mailto:muasacademy.ranchi@gmail.com" className="text-pink-600 hover:text-pink-700">muasacademy.ranchi@gmail.com</a>
                  </p>
                  <p><strong>Business Hours:</strong><br />Monday - Saturday: 10:00 AM - 7:00 PM<br />Sunday: Closed</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <iframe 
                  src="https://www.google.com/maps?q=C-127,+Harmu+Housing+Colony,+Harmu+Ranchi+834002&output=embed"
                  width="100%" 
                  height="300" 
                  style={{ border: 0 }}
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="MUAS Academy Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
